import React, { useEffect, useRef } from 'react';
import { Message } from '../types';
import { MetricsPanel } from './MetricsPanel';
import { MarkdownText } from './MarkdownText';

interface Props {
  messages: Message[];
  textOnly?: boolean;
}

export function ChatView({ messages, textOnly }: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div style={styles.empty}>
        <span style={styles.emptyIcon}>{textOnly ? '💬' : '🎙'}</span>
        <p style={styles.emptyText}>
          {textOnly ? 'Type a message to start' : 'Hold the button to speak'}
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {messages.map((msg) => (
        <div
          key={msg.id}
          style={{
            ...styles.row,
            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
          }}
        >
          <div style={styles.bubbleWrapper}>
            <div
              style={{
                ...styles.bubble,
                ...(msg.role === 'user' ? styles.userBubble : styles.assistantBubble),
              }}
            >
              {msg.role === 'assistant' ? <MarkdownText text={msg.text} /> : msg.text}
            </div>
            {msg.role === 'assistant' && msg.metrics && (
              <MetricsPanel metrics={msg.metrics} />
            )}
          </div>
        </div>
      ))}
      <div ref={bottomRef} />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  row: {
    display: 'flex',
    width: '100%',
  },
  bubbleWrapper: {
    maxWidth: '70%',
    display: 'flex',
    flexDirection: 'column',
  },
  bubble: {
    padding: '10px 14px',
    borderRadius: 18,
    fontSize: 15,
    lineHeight: 1.5,
    wordBreak: 'break-word',
  },
  userBubble: {
    background: '#4f46e5',
    color: '#fff',
    borderBottomRightRadius: 4,
  },
  assistantBubble: {
    background: '#f0f0f0',
    color: '#1a1a1a',
    borderBottomLeftRadius: 4,
  },
  empty: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    color: '#aaa',
  },
  emptyIcon: { fontSize: 48 },
  emptyText: { fontSize: 15, margin: 0 },
};
